import { TData, IColumn } from "../../components/table/data/DataTable";
import XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { CellHookData } from "jspdf-autotable";
import moment from "moment";
import AuthService from "../../services/auth/AuthService";
import {
    EPeriodicStatus,
    PeriodicDisplayDate,
} from "../../models/periodic/periodic";
import DownloadTable, { jsPDFWithPlugin } from "./downloadTable";

const DATE_COLUMN_SIZE = 1;
const DEFAULT = "#EBEBEB";
const SUCCESS = "#9FC88C";
const ERROR = "#E15241";
const INFO = "#3B86CB";
const WARNING = "#F2C24F";

class DownloadDataTable<T extends Object> extends DownloadTable {
    private type: string;
    private rows: TData<T>[];
    private columns: IColumn<TData<T>>[];
    private tableHeader: string[];
    private tableBody: string[][];
    private urlColumns: number[] = [];
    private urlRegex = /^(http|https):\/\/[^\s/$.?#].[^\s]*$/i;

    private readonly CELL_FONT_SIZE = 10;
    private readonly CELL_PADDING = 1.5;

    constructor(
        type: string,
        rows: TData<T>[],
        columns: IColumn<TData<T>>[],
        title: string,
        filters?: string[]
    ) {
        super(title, filters || []);
        this.type = type;
        this.rows = rows;
        this.columns = columns;

        this.tableHeader = this.columns.map((col) => col.headerName as string);
        this.tableBody = this.createTableBody();

        this.urlColumns = this.detectUrlColumns();
    }

    private ensureTableData() {
        if (!this.tableBody) {
            this.urlColumns = this.detectUrlColumns();
            this.tableBody = this.createTableBody();
        }
    }

    private createTableBody(): string[][] {
        return this.rows.map((row) => {
            return this.columns.map(({ field, transform }, colIndex) => {
                if (this.urlColumns?.includes(colIndex)) {
                    return String(row[field] || "");
                }
                if (transform) {
                    const transformed = transform.filterTransform(
                        row as TData<T>
                    );
                    return typeof transformed === "object"
                        ? (transformed as string[]).join(", ")
                        : (transformed as string);
                }
                return (row[field] as string) || "";
            });
        });
    }

    private detectUrlColumns(): number[] {
        return this.columns
            .map((col, index) => ({ index, field: col.field }))
            .filter(({ field }) => {
                return this.rows.some((row) => {
                    const value = row[field as keyof typeof row];
                    return value && this.isValidUrl(String(value));
                });
            })
            .map((col) => col.index);
    }

    private isValidUrl(url: string): boolean {
        return this.urlRegex.test(url);
    }

    private async loadImageData(): Promise<
        Map<string, { dataUrl: string; isSVG: boolean }>
    > {
        const imageMap = new Map<string, { dataUrl: string; isSVG: boolean }>();

        await Promise.all(
            this.rows.map(async (row, rowIndex) => {
                await Promise.all(
                    this.urlColumns!.map(async (colIndex) => {
                        const field = this.columns[colIndex].field;
                        const url = String(row[field as keyof typeof row]);
                        if (!this.isValidUrl(url)) return;

                        try {
                            const response = await fetch(url);
                            const contentType =
                                response.headers.get("content-type");
                            const isSVG =
                                contentType === "image/svg+xml" ||
                                url.toLowerCase().endsWith(".svg");

                            let dataUrl: string;
                            if (isSVG) {
                                const svgText = await response.text();
                                dataUrl = await this.svgToPngDataUrl(svgText);
                            } else {
                                const blob = await response.blob();
                                dataUrl = await new Promise<string>(
                                    (resolve) => {
                                        const reader = new FileReader();
                                        reader.onloadend = () =>
                                            resolve(reader.result as string);
                                        reader.readAsDataURL(blob);
                                    }
                                );
                            }

                            imageMap.set(`${rowIndex}-${colIndex}`, {
                                dataUrl,
                                isSVG,
                            });
                        } catch (error) {
                            console.error("Image processing failed:", url);
                            imageMap.set(`${rowIndex}-${colIndex}`, {
                                dataUrl: "",
                                isSVG: false,
                            });
                        }
                    })
                );
            })
        );

        return imageMap;
    }

    private async svgToPngDataUrl(svgText: string): Promise<string> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d");
                if (!ctx) return reject("Canvas context not found");

                canvas.width = img.width;
                canvas.height = img.height;
                ctx.drawImage(img, 0, 0);
                resolve(canvas.toDataURL("image/png"));
            };
            img.onerror = reject;
            img.src = `data:image/svg+xml;base64,${btoa(svgText)}`;
        });
    }

    GetPeriodicPDF(userLabel: string, dateLabel: string) {
        const tablePDF = new jsPDF() as jsPDFWithPlugin;

        tablePDF.setFontSize(this.TITLE_FONT_SIZE);
        tablePDF.setTextColor(this.TITLE_COLOR);

        this.addHeader(tablePDF);

        this.addFilters(tablePDF);

        // Table section
        const tableContent: string[][] = JSON.parse(
            JSON.stringify(this.tableBody)
        );

        const periodicDates: string[][] = [];
        const periodicStatus: EPeriodicStatus[][] = [];
        var maxLengthWord = "";

        const statusColorMap = new Map<
            EPeriodicStatus,
            { fillColor: string; textColor: string }
        >([
            [EPeriodicStatus.Todo, { fillColor: DEFAULT, textColor: "black" }],
            [
                EPeriodicStatus.Accepted,
                { fillColor: SUCCESS, textColor: "white" },
            ],
            [EPeriodicStatus.Refused, { fillColor: ERROR, textColor: "white" }],
            [EPeriodicStatus.ToVerify, { fillColor: INFO, textColor: "white" }],
            [
                EPeriodicStatus.Overdue,
                { fillColor: WARNING, textColor: "black" },
            ],
        ]);

        var trueIndex = 0;
        const lastColumnIndex = this.columns.length - 1;
        const bMargin = this.TABLE_BOTTOM_MARGIN;

        const cellFontSize = this.CELL_FONT_SIZE;
        const cellPadding = this.CELL_PADDING;

        const heightDivideFactor = 2.464909277644642;

        this.rows.map((row, i) => {
            this.columns.map(({ field, transform }, j) => {
                if (
                    transform &&
                    typeof transform.filterTransform(row) == "object"
                ) {
                    const periodicDatesCell = transform.filterTransform(
                        row
                    ) as string[];
                    const periodicStatusCell: EPeriodicStatus[] = [];

                    periodicDates.push(periodicDatesCell);
                    tableContent[i][j] = periodicDatesCell.join("\n");

                    const periodicDisplayDates = row[
                        field
                    ] as PeriodicDisplayDate[];
                    periodicDisplayDates.map(({ status }) => {
                        periodicStatusCell.push(status);
                    });
                    periodicStatus.push(periodicStatusCell);

                    periodicDatesCell.map((date) => {
                        if (
                            tablePDF.getTextWidth(date) >
                            tablePDF.getTextWidth(maxLengthWord)
                        )
                            maxLengthWord = date;
                    });
                }
            });
        });

        tablePDF.autoTable({
            startY:
                this.TABLE_START_Y -
                15 +
                this.FILTERS_MARGIN * this.filters.length,
            head: [this.tableHeader],
            body: tableContent,
            bodyStyles: { cellPadding: cellPadding, fontSize: cellFontSize },
            margin: { bottom: bMargin },
            didParseCell: function (data: CellHookData) {
                const periodicCellWidth =
                    tablePDF.getTextWidth(maxLengthWord) + cellPadding * 2;

                if (
                    periodicStatus.length > 0 &&
                    data.cell.section === "body" &&
                    data.row.index >= 0
                ) {
                    if (data.column.index === lastColumnIndex) {
                        data.cell.styles.minCellWidth =
                            DATE_COLUMN_SIZE * periodicCellWidth;
                    }
                }
            },
            didDrawCell: function (data: CellHookData) {
                const periodicCellWidth =
                    tablePDF.getTextWidth(maxLengthWord) + cellPadding * 2;
                const periodicHeight = cellFontSize / heightDivideFactor;

                if (data.row.index >= 0 && data.cell.section === "body")
                    trueIndex = data.row.index;

                if (
                    periodicDates.length > 0 &&
                    data.column.index === lastColumnIndex &&
                    data.cell.section === "body"
                ) {
                    const pdfDateTable: string[][] = [];
                    const pdfStatusTable: EPeriodicStatus[][] = [];

                    while (
                        periodicDates[trueIndex].length &&
                        data.cell.y +
                            (pdfDateTable.length + 1) * periodicHeight +
                            cellPadding * 2 <
                            tablePDF.internal.pageSize.height - bMargin
                    ) {
                        pdfDateTable.push(
                            periodicDates[trueIndex].splice(0, DATE_COLUMN_SIZE)
                        );
                        pdfStatusTable.push(
                            periodicStatus[trueIndex].splice(
                                0,
                                DATE_COLUMN_SIZE
                            )
                        );
                    }

                    pdfStatusTable.map((statusRow, i) => {
                        statusRow.map((status, j) => {
                            tablePDF.setDrawColor("black");
                            tablePDF.setFillColor(
                                statusColorMap.get(status)?.fillColor || DEFAULT
                            );
                            tablePDF.setTextColor(
                                statusColorMap.get(status)?.textColor || "black"
                            );

                            tablePDF.roundedRect(
                                data.cell.x,
                                data.cell.y + cellPadding + periodicHeight * i,
                                periodicCellWidth,
                                periodicHeight,
                                1,
                                1,
                                "FD"
                            );
                            tablePDF.text(
                                pdfDateTable[i][j],
                                data.cell.x + cellPadding,
                                data.cell.y +
                                    cellPadding +
                                    periodicHeight * i +
                                    3
                            );
                        });
                    });
                }
            },
        });

        this.addFooter(tablePDF, userLabel, dateLabel);

        tablePDF.save(this.type + "_data.pdf");
    }

    GetExcel(userLabel: string, dateLabel: string) {
        this.ensureTableData();
        const completedTable = [this.tableHeader].concat(this.tableBody!);
        const worksheet = XLSX.utils.aoa_to_sheet([]);

        const user = `${userLabel}: ${AuthService.user?.name}`;
        const exportDate = `${dateLabel}: ${moment().utc().local().format("LLL")}`;
        XLSX.utils.sheet_add_aoa(
            worksheet,
            [[this.title], [user], [exportDate]],
            { origin: "B2" }
        );

        XLSX.utils.sheet_add_aoa(worksheet, completedTable, { origin: "B7" });

        const CELL_MAX_LENGTH = 35;
        const TABLE_START_ROW = 6;
        const TABLE_START_COLUMN = 1;

        // Set column length
        const columnLengths = [{ wch: 10 }, { wch: this.title.length * 2 }];
        completedTable.map((row, _) => {
            row.map((cell, j) => {
                if (columnLengths.length <= j + TABLE_START_COLUMN)
                    columnLengths.push({ wch: 0 });
                if (columnLengths[j + TABLE_START_COLUMN].wch < cell.length)
                    columnLengths[j + TABLE_START_COLUMN] = {
                        wch: Math.min(cell.length, CELL_MAX_LENGTH),
                    };
            });
        });
        worksheet["!cols"] = columnLengths;

        const TITLE_SIZE = 24;
        const titleFont = {
            bold: true,
            sz: TITLE_SIZE,
            color: { rgb: "1F497D" },
        };
        const borderColor = { color: { rgb: "505050" } };
        const TEXT_SIZE = 12;
        const headerFont = { bold: true, color: { rgb: "FFFFFF" } };
        const headerFill = { fgColor: { rgb: "1F497D" }, patternType: "solid" };

        const rowHeights = [{ hpt: TEXT_SIZE }, { hpt: TITLE_SIZE }];
        worksheet["!rows"] = rowHeights;

        worksheet["B2"].s = {
            font: titleFont,
            border: { left: borderColor, top: borderColor, right: borderColor },
        };
        worksheet["B3"].s = {
            font: { sz: TEXT_SIZE },
            border: { left: borderColor, right: borderColor },
        };
        worksheet["B4"].s = {
            font: { sz: TEXT_SIZE },
            border: {
                left: borderColor,
                bottom: borderColor,
                right: borderColor,
            },
        };

        // Table header font and background
        for (var j = 0; j < this.columns.length + 0; j++) {
            worksheet[
                XLSX.utils.encode_cell({
                    r: TABLE_START_ROW,
                    c: j + TABLE_START_COLUMN,
                })
            ].s = { font: headerFont, fill: headerFill };
        }

        for (var i = 0; i < completedTable.length; i++) {
            // Column borders
            const firstColumn =
                worksheet[
                    XLSX.utils.encode_cell({
                        r: TABLE_START_ROW + i,
                        c: TABLE_START_COLUMN,
                    })
                ];
            if (!firstColumn.s)
                firstColumn.s = { ...firstColumn.s, border: {} };
            firstColumn.s = {
                ...firstColumn.s,
                border: { ...firstColumn.s.border, left: borderColor },
            };

            const lastColumn =
                worksheet[
                    XLSX.utils.encode_cell({
                        r: TABLE_START_ROW + i,
                        c: TABLE_START_COLUMN + completedTable[0].length - 1,
                    })
                ];
            if (!lastColumn.s) lastColumn.s = { ...lastColumn.s, border: {} };
            lastColumn.s = {
                ...lastColumn.s,
                border: { ...lastColumn.s.border, right: borderColor },
            };

            // Table cell font and alignment
            for (var j = 0; j < completedTable[0].length; j++) {
                const cell =
                    worksheet[
                        XLSX.utils.encode_cell({
                            r: TABLE_START_ROW + i,
                            c: TABLE_START_COLUMN + j,
                        })
                    ];
                if (!cell.s) cell.s = { ...cell.s, font: {} };
                cell.s = {
                    ...cell.s,
                    font: { ...cell.s.font, sz: TEXT_SIZE },
                    alignment: { vertical: "top", wrapText: true },
                };
            }
        }

        for (var j = 0; j < completedTable[0].length; j++) {
            // Row borders
            const firstRow =
                worksheet[
                    XLSX.utils.encode_cell({
                        r: TABLE_START_ROW,
                        c: TABLE_START_COLUMN + j,
                    })
                ];
            if (!firstRow.s) firstRow.s = { ...firstRow.s, border: {} };
            firstRow.s = {
                ...firstRow.s,
                border: { ...firstRow.s.border, top: borderColor },
            };

            const lastRow =
                worksheet[
                    XLSX.utils.encode_cell({
                        r: TABLE_START_ROW + completedTable.length - 1,
                        c: TABLE_START_COLUMN + j,
                    })
                ];
            if (!lastRow.s) lastRow.s = { ...lastRow.s, border: {} };
            lastRow.s = {
                ...lastRow.s,
                border: { ...lastRow.s.border, bottom: borderColor },
            };
        }

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(
            workbook,
            worksheet,
            this.type + "TableSheet"
        );

        const excelBuffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array",
        });
        const blob = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
        });

        saveAs(blob, this.type + "_data.xlsx");
    }

    GetCSV() {
        this.ensureTableData();
        const completedTable = [this.tableHeader].concat(this.tableBody!);
        var csvString = "";

        completedTable.map((row) => {
            csvString = csvString.concat(row.join(";"));
            csvString = csvString.concat("\n");
        });

        const blob = new Blob([csvString], { type: "text/csv;charset=utf-8," });
        saveAs(blob, this.type + "_data.csv");
    }

    async GetPDF(userLabel: string, dateLabel: string): Promise<void> {
        this.ensureTableData();
        const pdf = new jsPDF({ orientation: "portrait" }) as jsPDFWithPlugin;

        pdf.setFontSize(this.TITLE_FONT_SIZE);
        pdf.setTextColor(this.TITLE_COLOR);

        this.addHeader(pdf);
        this.addFilters(pdf);

        const imageColIndex = this.urlColumns![0]; // Assuming floorURL is the first URL column
        const columnStyles = {
            [imageColIndex]: {
                cellWidth: 30,
                minCellHeight: 20,
                cellPadding: 1,
            },
            ...Object.fromEntries(
                this.columns
                    .map((_, idx) => [
                        idx,
                        {
                            cellWidth: "auto",
                            fontSize: 9,
                            cellPadding: 1,
                            overflow: "linebreak",
                            halign: "center",
                            valign: "middle",
                        },
                    ])
                    .filter(([idx]) => idx !== imageColIndex)
            ),
        };

        const imageDataMap = await this.loadImageData();

        pdf.autoTable({
            startY:
                this.TABLE_START_Y -
                20 +
                this.FILTERS_MARGIN * this.filters.length,
            head: [this.tableHeader],
            body: this.tableBody!,
            columnStyles,
            styles: {
                fontSize: 9,
                halign: "center",
                valign: "middle",
            },
            margin: { bottom: this.TABLE_BOTTOM_MARGIN },
            didDrawCell: (data) => {
                if (
                    this.urlColumns!.includes(data.column.index) &&
                    data.section === "body"
                ) {
                    const imgData = imageDataMap.get(
                        `${data.row.index}-${data.column.index}`
                    );
                    if (imgData?.dataUrl) {
                        const padding = 1;
                        const availableWidth = data.cell.width - padding * 2;
                        const availableHeight = data.cell.height - padding * 2;

                        const imgProps = pdf.getImageProperties(
                            imgData.dataUrl
                        );
                        let targetWidth = availableWidth;
                        let targetHeight = availableHeight;

                        if (imgData.isSVG) {
                            const targetRatio = 16 / 9;
                            targetHeight = Math.min(
                                availableHeight,
                                availableWidth / targetRatio
                            );
                            targetWidth = targetHeight * targetRatio;
                        } else {
                            const imgRatio = imgProps.width / imgProps.height;
                            const availableRatio =
                                availableWidth / availableHeight;
                            if (imgRatio > availableRatio) {
                                targetHeight = availableWidth / imgRatio + 8;
                            } else {
                                targetWidth = availableHeight * imgRatio + 8;
                            }
                        }

                        const x =
                            data.cell.x +
                            padding +
                            (availableWidth - targetWidth) / 2;
                        const y =
                            data.cell.y +
                            padding +
                            (availableHeight - targetHeight) / 2;

                        pdf.addImage(
                            imgData.dataUrl,
                            "PNG",
                            x,
                            y,
                            targetWidth,
                            targetHeight
                        );
                    }
                }
            },
        });

        this.addFooter(pdf, userLabel, dateLabel);
        pdf.save(`${this.type}_report.pdf`);
    }
}

export default DownloadDataTable;
